import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom'
import { toast } from 'sonner'

import Card from '../ui/Card'
import Button from '../ui/Button'
import PageLoader from '../ui/PageLoader'
import PageHeader from '../layout/PageHeader'
import ProjectPageTitle from '../layout/ProjectPageTitle'
import { formatPipeSetDimensions, joinMetaParts } from '../../utils/projectSubtitle'
import PdfViewer from '../ui/PdfViewer'
import ConfirmDialog, { type ConfirmDialogTone } from '../ui/ConfirmDialog'

import { useProjects } from '../../contexts/ProjectsContext'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import { ApiError } from '../../config/api'
import {
  fetchWpsPreviewData,
  type WpsPreviewResponse,
  type WpsPreviewTemplateData
} from '../../utils/wpsPreviewClient'
import { generateWpsReport } from '../../utils/reportsClient'
import { fetchTeamMembers } from '../../api/teamMembersApi'
import type { TeamMember } from '../../types/teamMember'
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import {
  getBevelIdAtIndex,
  findIndexByBevelId,
  stampBevelIdsIfMissing
} from '../../utils/bevelPassesIdentity'

import WpsIdentifiersModal, {
  type WpsIdentifiersValue
} from './wpsPreview/WpsIdentifiersModal'
import WpsApprovalsModal, {
  type WpsApprovalDraft
} from './wpsPreview/WpsApprovalsModal'
import WpsPreviewCanvas from './wpsPreview/WpsPreviewCanvas'
import WpsPreviewToolbar from './wpsPreview/WpsPreviewToolbar'
import WpsPreviewHeaderPills from './wpsPreview/WpsPreviewHeaderPills'
import WpsDiffConfirmDialog, {
  type WpsConfirmMode
} from './wpsPreview/WpsDiffConfirmDialog'
import WpsNavigateConfirmModal from './wpsPreview/WpsNavigateConfirmModal'
import {
  buildWpsPreviewSignificantDiff,
  hasSignificantWpsPreviewChanges
} from './wpsPreview/wpsPreviewDiff'
import { useWpsPreviewStaging } from './wpsPreview/useWpsPreviewStaging'
import type {
  WpsFieldLinkContext,
  WpsFieldNavTarget
} from './wpsPreview/wpsFieldLinkRegistry'

// ---------------------------------------------------------------------------
// WPS editable preview page (slice 08).
//
// Loads /reports/wps-preview, renders an HTML/CSS canvas that mirrors
// the PDF, and lets the user edit identifiers + approvals + notes
// inline before hitting "Save" or "Save & Generate".
//
// Staging strategy is intentionally simpler than PqrPreviewPage's
// (no per-section diff tracking, no sessionStorage persistence): the
// WPS document is procedure-wide and small, so a single in-memory
// snapshot keyed off the loaded server `version` covers the v1 UX.
// ---------------------------------------------------------------------------

export interface WpsEditableSnapshot {
  identifiers: WpsIdentifiersValue
  approvals: WpsApprovalDraft[]
  notes: WpsPreviewTemplateData['notes']
}

function snapshotFromPreview(data: WpsPreviewTemplateData): WpsEditableSnapshot {
  return {
    identifiers: {
      wpsNumber: data.identifiers.wpsNumber === '\u2014' ? '' : data.identifiers.wpsNumber,
      revision: data.identifiers.revision === '\u2014' ? '' : data.identifiers.revision,
      // The mapper folds application + equipment into topMeta cells —
      // pull them back out by label so the editor round-trips cleanly.
      application:
        firstCellValue(data.topMeta, 'Application:') === '\u2014'
          ? ''
          : firstCellValue(data.topMeta, 'Application:'),
      equipmentDescription:
        firstCellValue(data.topMeta, 'Equipment:') === '\u2014'
          ? ''
          : firstCellValue(data.topMeta, 'Equipment:')
    },
    approvals: data.approvals.map((a) => ({
      id: a.id,
      label: a.label,
      name: a.name,
      title: a.title,
      date: a.date,
      signature: a.signatureImage,
      stampImage: a.stampImage,
      memberId: a.memberId
    })),
    notes: data.notes.map((n) => ({ id: n.id, body: n.body }))
  }
}

function firstCellValue(
  topMeta: WpsPreviewTemplateData['topMeta'],
  label: string
): string {
  for (const row of topMeta) {
    for (const cell of row) {
      if (cell === null) continue
      if (cell.label === label) return cell.value
    }
  }
  return ''
}

function snapshotsEqual(a: WpsEditableSnapshot, b: WpsEditableSnapshot): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

export default function WpsPreviewPage() {
  const { id: projectId, pipeId: pipeSetId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { getProject } = useProjects()
  const { getSection, updateSection, version: sectionsVersion } = useWpsSections()
  const project = projectId ? getProject(projectId) : undefined
  const pipeSet = project?.pipeSets.find((p) => p.id === pipeSetId)

  // wps-history-bevel-scoped issue 07: URL params seed which bevel is
  // active + (for synthetic next-version rows) the next WPS#/rev to
  // pre-fill the staging snapshot. All four are optional — visiting
  // the preview without any params loads the persisted snapshot
  // verbatim, exactly as before issue 07.
  //
  // `bevelIndex` is a self-healing fallback added when the preview URL
  // carries a `bevelId` that doesn't resolve against the persisted
  // `sections.pass.bevelPasses[]` (e.g. legacy pipe-sets where the
  // bevel entries were never stamped with stable IDs). The caller
  // (`WpsDetails.handlePreview`) writes both whenever it knows them.
  const urlBevelId = searchParams.get('bevelId') ?? ''
  const urlBevelIndex = searchParams.get('bevelIndex') ?? ''
  const urlSeededWpsNumber = searchParams.get('wpsNumber') ?? ''
  const urlSeededRevision = searchParams.get('revision') ?? ''
  const urlWeldIdsRaw = searchParams.get('weldIds') ?? ''
  const urlPqrNumbersRaw = searchParams.get('pqrNumbers') ?? ''
  const isPdfImportPreview = searchParams.get('import') === '1'

  const activeWeldIds = useMemo(() => {
    if (urlWeldIdsRaw.trim()) {
      return urlWeldIdsRaw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    }
    return [] as string[]
  }, [urlWeldIdsRaw])

  const activePqrNumbers = useMemo(() => {
    if (urlPqrNumbersRaw.trim()) {
      return urlPqrNumbersRaw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    }
    return [] as string[]
  }, [urlPqrNumbersRaw])

  // wps-history-bevel-scoped issue 04: resolve the *active* bevel's
  // stable id so the generate-wps request body carries `bevelId`. The
  // active index is whatever the Pass Sequence form last persisted
  // (`sections.pass.activeBevelIndex`); the id lookup is index-based
  // via `getBevelIdAtIndex`. If the lookup fails (legacy section that
  // never re-saved, deleted bevel, etc.) we deliberately omit
  // `bevelId` from the request rather than throwing — generate still
  // works, the row just lands in the aggregated history bucket. A
  // dev-mode warn surfaces the case in QA.
  const { activeBevelId, activeBevelLabel } = useMemo(() => {
    const passSection = getSection('pass') as unknown
    if (
      passSection === null ||
      typeof passSection !== 'object' ||
      Array.isArray(passSection)
    ) {
      return { activeBevelId: undefined as string | undefined, activeBevelLabel: '' }
    }
    const obj = passSection as { activeBevelIndex?: unknown }
    const activeIndex =
      typeof obj.activeBevelIndex === 'number' && Number.isFinite(obj.activeBevelIndex)
        ? Math.max(0, Math.trunc(obj.activeBevelIndex))
        : 0
    const id = getBevelIdAtIndex(passSection, activeIndex)
    return {
      activeBevelId: id,
      activeBevelLabel: `Bevel ${activeIndex + 1}`
    }
  }, [getSection])

  // wps-history-bevel-scoped issue 07: on mount, if `?bevelId=` is
  // present and resolvable, switch the persisted `activeBevelIndex`
  // so the preview canvas re-renders that bevel's parameters. Run
  // exactly once per (bevelId, bevelIndex) URL pair (guarded by a
  // ref) to avoid an infinite loop: `updateSection` mutates
  // `getSection` which would re-trigger this effect.
  //
  // Self-heal flow (added in the bevel-scoping fix): when the URL
  // `bevelId` doesn't resolve against persisted `bevelPasses[]` (a
  // common case when the user generated the preview link from a
  // page-local stamped id), fall back to the `?bevelIndex=N` hint
  // also placed on the URL by `WpsDetails.handlePreview`. On either
  // success path, stamp any unstamped `bevelPasses[]` entries and
  // persist them back so subsequent saves can carry a real `bevelId`
  // (otherwise the report would be bucketed into Bevel 1 by the
  // orphan-fold branch in `groupWpsReportsForHistory`).
  const seededBevelIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (activeWeldIds.length > 0) return
    if (!urlBevelId && !urlBevelIndex) return
    const seedKey = `${urlBevelId}|${urlBevelIndex}`
    if (seededBevelIdRef.current === seedKey) return

    const passSection = getSection('pass') as unknown
    if (
      passSection === null ||
      typeof passSection !== 'object' ||
      Array.isArray(passSection)
    ) {
      seededBevelIdRef.current = seedKey
      return
    }
    const obj = passSection as {
      activeBevelIndex?: unknown
      bevelPasses?: unknown
    }
    const bevelPassesArr = Array.isArray(obj.bevelPasses) ? obj.bevelPasses : []

    let targetIdx = urlBevelId ? findIndexByBevelId(passSection, urlBevelId) : -1
    if (targetIdx < 0 && urlBevelIndex) {
      const parsed = parseInt(urlBevelIndex, 10)
      if (
        Number.isInteger(parsed) &&
        parsed >= 0 &&
        parsed < bevelPassesArr.length
      ) {
        targetIdx = parsed
      }
    }
    if (targetIdx < 0) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          `[WpsPreviewPage] Could not resolve URL bevelId="${urlBevelId}" / bevelIndex="${urlBevelIndex}" against sections.pass.bevelPasses[]; falling back to current active bevel.`
        )
      }
      seededBevelIdRef.current = seedKey
      return
    }

    const rawBevelPasses = bevelPassesArr.filter(
      (entry): entry is Record<string, unknown> =>
        entry !== null && typeof entry === 'object' && !Array.isArray(entry)
    )
    const stamped = stampBevelIdsIfMissing(rawBevelPasses)
    const idsChanged = stamped.some(
      (entry, i) => entry.id !== rawBevelPasses[i]?.id
    )
    const currentActive =
      typeof obj.activeBevelIndex === 'number' && Number.isFinite(obj.activeBevelIndex)
        ? Math.max(0, Math.trunc(obj.activeBevelIndex))
        : 0

    if (idsChanged || currentActive !== targetIdx) {
      void updateSection('pass', {
        ...passSection,
        bevelPasses: stamped,
        activeBevelIndex: targetIdx
      })
    }
    seededBevelIdRef.current = seedKey
  }, [activeWeldIds.length, urlBevelId, urlBevelIndex, getSection, updateSection])

  // Session-storage staging (slice 08 of wps-pdf-xlsx-faithful).
  // Click-through to a section form and back must preserve in-flight
  // identifier / approvals / notes edits — `useWpsPreviewStaging`
  // owns that buffer under a key scoped by (projectId, pipeSetId).
  const stagingHook = useWpsPreviewStaging({
    projectId: projectId ?? 'unknown',
    pipeSetId: pipeSetId ?? 'unknown'
  })

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [members, setMembers] = useState<TeamMember[]>([])
  const [response, setResponse] = useState<WpsPreviewResponse | null>(null)
  const [baseline, setBaseline] = useState<WpsEditableSnapshot | null>(null)
  const [staged, setStagedLocal] = useState<WpsEditableSnapshot | null>(
    stagingHook.staged
  )

  // Mirror every staged-state change into sessionStorage so the
  // round-trip survives a hard navigation.
  const setStaged = useCallback(
    (updater: React.SetStateAction<WpsEditableSnapshot | null>) => {
      setStagedLocal((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (prev: WpsEditableSnapshot | null) => WpsEditableSnapshot | null)(prev)
            : updater
        stagingHook.setStaged(next)
        return next
      })
    },
    [stagingHook]
  )

  const [editingIdentifiers, setEditingIdentifiers] = useState(false)
  const [editingApprovals, setEditingApprovals] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // Slice C.2 of wps-product-parity-fixes — surface a persistent
  // banner (rather than a one-shot toast) when the server reports
  // the locally-staged version is stale, mirroring PqrPreviewPage's
  // `staleBanner` flow. Also drive the discard / reload-baseline
  // confirms through a shared `ConfirmDialog` so the page no longer
  // silently mutates state.
  const [staleBanner, setStaleBanner] = useState<string | null>(null)
  // Slice C.3 of wps-product-parity-fixes — both Save and
  // Save & Generate route through `WpsDiffConfirmDialog` first. Null
  // means the dialog is closed; either 'save' or 'saveAndGenerate'
  // means it's open in that mode.
  const [confirmMode, setConfirmMode] = useState<WpsConfirmMode | null>(null)
  const [confirmEmailOnly, setConfirmEmailOnly] = useState(false)
  // Slice C.4 of wps-product-parity-fixes — every URL-target field
  // link click routes through this guard so the user can be warned
  // before staged edits are abandoned. `null` means the modal is
  // closed; otherwise the request describes which field was clicked
  // and how to proceed if the user confirms.
  const [navRequest, setNavRequest] = useState<{
    target: WpsFieldNavTarget
    fieldLabel: string
    proceed: () => void
  } | null>(null)
  const [pageConfirm, setPageConfirm] = useState<{
    title: string
    description: string
    confirmLabel: string
    tone?: ConfirmDialogTone
    onConfirm: () => void
  } | null>(null)
  // Slice C.5 of wps-product-parity-fixes — Save & Generate now opens
  // the freshly-generated PDF in the in-app `PdfViewer` overlay (same
  // surface used by `WpsDetails > Download`) instead of redirecting
  // away from the preview. The user reviews then chooses Download or
  // Open-in-New-Tab from the viewer header without losing the staged
  // context they were just editing.
  const [viewer, setViewer] = useState<{ fileUrl: string; fileName: string } | null>(
    null
  )

  const isDirty = useMemo(() => {
    if (!baseline || !staged) return false
    return !snapshotsEqual(baseline, staged)
  }, [baseline, staged])

  const hasSignificantChanges = useMemo(() => {
    if (!baseline || !staged) return false
    return hasSignificantWpsPreviewChanges(baseline, staged)
  }, [baseline, staged])

  const significantDiff = useMemo(() => {
    if (!baseline || !staged) return { groups: [], hasChanges: false }
    return buildWpsPreviewSignificantDiff(baseline, staged)
  }, [baseline, staged])

  useUnsavedChangesGuard(() => hasSignificantChanges)

  const load = useCallback(async () => {
    if (!projectId || !pipeSetId) return
    setLoading(true)
    setLoadError(null)
    try {
      const hasScope =
        activeWeldIds.length > 0 || activePqrNumbers.length > 0
      const [payload, team] = await Promise.all([
        fetchWpsPreviewData(
          projectId,
          pipeSetId,
          hasScope
            ? {
                ...(activeWeldIds.length > 0 ? { weldIds: activeWeldIds } : {}),
                ...(activePqrNumbers.length > 0 ? { pqrNumbers: activePqrNumbers } : {})
              }
            : null
        ),
        fetchTeamMembers(projectId, { status: 'active', page: 1, pageSize: 200 })
      ])
      setMembers(team.items.filter((m) => m.status === 'active'))
      setResponse(payload)
      const snap = snapshotFromPreview(payload.data)
      setBaseline(snap)
      // wps-history-bevel-scoped issue 07: when the user lands here
      // from a synthetic "next version" row in the WPS history
      // (`?new=1&wpsNumber=…&revision=…`), pre-fill the staging
      // snapshot with those values so the preview reads `v(N+1)`
      // even though `sections.wps` still holds the previous `vN`.
      //
      // History v3 fix: also seed the identifiers when the user
      // clicked a non-synthetic card from the WPS history grid.
      // `WpsDetails.handlePreview` now writes `wpsNumber`/`revision`
      // for every card click; the `new=1` flag is no longer required
      // here. Plain `?bevelId=…` with no identifier params keeps
      // loading the persisted snapshot verbatim — the preview-as-edit
      // entry point used by direct deep-links and breadcrumb clicks.
      const seeded: WpsEditableSnapshot =
        urlSeededWpsNumber.length > 0 && urlSeededRevision.length > 0
          ? {
              ...snap,
              identifiers: {
                ...snap.identifiers,
                wpsNumber: urlSeededWpsNumber,
                revision: urlSeededRevision
              }
            }
          : snap
      // Preserve session-storage-staged edits across navigation
      // round-trips. If nothing is staged, fall through to the
      // (possibly URL-seeded) snapshot so first load behaves the
      // same as before for non-`new` visits.
      setStagedLocal((existing) => existing ?? stagingHook.staged ?? seeded)

      // History v3 fix: strip the seed-only params (`new`,
      // `wpsNumber`, `revision`) once the preview has been seeded so
      // a refresh after Save & Generate doesn't re-apply them on top
      // of the freshly persisted baseline. `bevelId` / `bevelIndex`
      // are preserved — they identify the active bevel scope and
      // need to survive a refresh. Mirrors the PQR pattern at
      // `PqrPreviewPage.load` (gated by `newDraftRequested`).
      // Idempotent: only re-write when the stripped URL actually
      // differs (StrictMode mounts the effect twice).
      const next = new URLSearchParams(searchParams)
      let mutated = false
      for (const k of ['new', 'wpsNumber', 'revision']) {
        if (next.has(k)) {
          next.delete(k)
          mutated = true
        }
      }
      if (mutated && next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true })
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load WPS preview')
    } finally {
      setLoading(false)
    }
  }, [
    projectId,
    pipeSetId,
    stagingHook.staged,
    urlSeededWpsNumber,
    urlSeededRevision,
    activeWeldIds,
    activePqrNumbers,
    searchParams,
    setSearchParams
  ])

  useEffect(() => {
    void load()
  }, [load])

  const performDiscard = useCallback(() => {
    if (baseline) setStaged(baseline)
    stagingHook.clearStaged()
  }, [baseline, setStaged, stagingHook])

  const handleDiscard = () => {
    if (!hasSignificantChanges) {
      performDiscard()
      return
    }
    setPageConfirm({
      title: 'Discard staged changes?',
      description:
        'All staged WPS edits will be reverted to the last saved values. This cannot be undone.',
      confirmLabel: 'Discard',
      tone: 'danger',
      onConfirm: () => {
        performDiscard()
        setPageConfirm(null)
      }
    })
  }

  const submit = useCallback(
    async (generatePdf: boolean, options?: { notificationEmails?: string[] }) => {
      if (!projectId || !pipeSetId || !staged || !response) return
      if (
        generatePdf &&
        (!staged.identifiers.wpsNumber.trim() || !staged.identifiers.revision.trim())
      ) {
        toast.error('WPS No. and Revision are required to generate the PDF.')
        return
      }
      setSubmitting(true)
      try {
        // Defensive stamp+persist guard: when the seeding effect didn't
        // get a chance to durable-ify the bevelPasses IDs (race with
        // the user clicking Save immediately, or sections that have
        // never been normalised by WpsPassDetails), do it here once
        // before generating so the request always carries the correct
        // `bevelId`. Without this, the orphan-fold branch in
        // `groupWpsReportsForHistory` would silently bucket the saved
        // report into Bevel 1 regardless of which bevel the user was
        // actually previewing.
        let effectiveBevelId: string | undefined
        if (activeWeldIds.length === 0) {
          effectiveBevelId = activeBevelId
          if (!effectiveBevelId) {
            const passSection = getSection('pass') as unknown
            if (
              passSection !== null &&
              typeof passSection === 'object' &&
              !Array.isArray(passSection)
            ) {
              const obj = passSection as {
                activeBevelIndex?: unknown
                bevelPasses?: unknown
              }
              const bevelPassesArr = Array.isArray(obj.bevelPasses)
                ? obj.bevelPasses
                : []
              const rawBevelPasses = bevelPassesArr.filter(
                (entry): entry is Record<string, unknown> =>
                  entry !== null &&
                  typeof entry === 'object' &&
                  !Array.isArray(entry)
              )
              if (rawBevelPasses.length > 0) {
                const stamped = stampBevelIdsIfMissing(rawBevelPasses)
                const idsChanged = stamped.some(
                  (entry, i) => entry.id !== rawBevelPasses[i]?.id
                )
                const activeIndex =
                  typeof obj.activeBevelIndex === 'number' &&
                  Number.isFinite(obj.activeBevelIndex)
                    ? Math.max(0, Math.trunc(obj.activeBevelIndex))
                    : 0
                if (idsChanged) {
                  try {
                    await updateSection('pass', {
                      ...passSection,
                      bevelPasses: stamped
                    })
                  } catch (err) {
                    // eslint-disable-next-line no-console
                    console.warn(
                      '[WpsPreviewPage] Failed to persist stamped bevelPasses IDs before save',
                      err
                    )
                  }
                }
                const stampedEntry = stamped[activeIndex]
                if (
                  stampedEntry &&
                  typeof stampedEntry.id === 'string' &&
                  stampedEntry.id.length > 0
                ) {
                  effectiveBevelId = stampedEntry.id
                }
              }
            }
          }
        }
        let ifMatchVersion = response.version
        try {
          const freshPreview = await fetchWpsPreviewData(
            projectId,
            pipeSetId,
            activeWeldIds.length > 0 || activePqrNumbers.length > 0
              ? {
                  ...(activeWeldIds.length > 0 ? { weldIds: activeWeldIds } : {}),
                  ...(activePqrNumbers.length > 0 ? { pqrNumbers: activePqrNumbers } : {})
                }
              : null
          )
          ifMatchVersion = Math.max(ifMatchVersion, freshPreview.version, sectionsVersion)
        } catch {
          ifMatchVersion = Math.max(ifMatchVersion, sectionsVersion)
        }
        const result = await generateWpsReport({
          projectId,
          pipeSetId,
          body: {
            generatePdf,
            ifMatchVersion: ifMatchVersion > 0 ? ifMatchVersion : null,
            wpsNumber: staged.identifiers.wpsNumber.trim(),
            revision: staged.identifiers.revision.trim(),
            application: staged.identifiers.application,
            equipmentDescription: staged.identifiers.equipmentDescription,
            approvals: staged.approvals.map((a) => ({
              id: a.id,
              label: a.label,
              name: a.name,
              title: a.title,
              date: a.date,
              signature: a.signature,
              stampImage: a.stampImage,
              signedAt: a.signedAt,
              memberId: a.memberId
            })),
            notes: staged.notes.map((n) => ({ id: n.id, body: n.body })),
            ...(activeWeldIds.length > 0
              ? {
                  weldIds: activeWeldIds,
                  ...(activePqrNumbers.length > 0
                    ? { pqrNumbers: activePqrNumbers }
                    : {})
                }
              : effectiveBevelId
                ? { bevelId: effectiveBevelId }
                : {}),
            notificationEmails:
              generatePdf && options?.notificationEmails?.length
                ? options.notificationEmails
                : undefined
          }
        })
        setBaseline(staged)
        setResponse({ ...response, version: result.sectionsVersion })
        // Drop the session-storage cache once the server confirms
        // the save — keeping it would re-stage the same edits on
        // the next visit.
        stagingHook.clearStaged()
        // Slice C.5 of wps-product-parity-fixes — open the freshly-
        // generated PDF in the in-app `PdfViewer` overlay instead of
        // redirecting to the history page. The user stays on the
        // preview, can re-open identifiers / approvals to iterate,
        // and the report still shows up in history on the next visit
        // because the synthetic placeholder logic auto-produces the
        // next `v(N+2)` row from the freshly-bumped revision.
        if (generatePdf && result.report) {
          setViewer({
            fileUrl: result.report.downloadUrl,
            fileName: result.report.name
          })
          toast.success('WPS saved. PDF ready.')
        } else {
          toast.success('WPS draft saved.')
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to save WPS.'
        if (
          (err instanceof ApiError && err.status === 409) ||
          msg.toLowerCase().includes('stale') ||
          msg.toLowerCase().includes('newer') ||
          msg.toLowerCase().includes('conflict')
        ) {
          setStaleBanner(
            'The server has a newer version of this WPS. Reload the baseline to pull the latest values before saving again.'
          )
        } else {
          toast.error(msg)
        }
      } finally {
        setSubmitting(false)
      }
    },
    [
      projectId,
      pipeSetId,
      staged,
      response,
      activeBevelId,
      activeWeldIds,
      activePqrNumbers,
      stagingHook,
      getSection,
      updateSection,
      sectionsVersion
    ]
  )

  const requestSave = useCallback(
    (mode: WpsConfirmMode) => {
      if (mode === 'saveAndGenerate' && !hasSignificantChanges) {
        setConfirmEmailOnly(true)
        setConfirmMode('saveAndGenerate')
        return
      }
      setConfirmEmailOnly(false)
      if (hasSignificantChanges) {
        setConfirmMode(mode)
        return
      }
      if (!isDirty && mode === 'save') return
      void submit(mode === 'saveAndGenerate', { notificationEmails: [] })
    },
    [hasSignificantChanges, isDirty, submit]
  )

  const closeConfirmDialog = useCallback(() => {
    setConfirmMode(null)
    setConfirmEmailOnly(false)
  }, [])

  if (loading) return <PageLoader />
  if (loadError || !response || !staged || !project || !pipeSet) {
    return (
      <Card title="WPS preview unavailable">
        <p className="text-sm text-gray-600">
          {loadError ?? 'The project or pipe set could not be found.'}
        </p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Back to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  // Compose the canvas-facing data: merge the server preview payload
  // with the user's staged identifiers / approvals / notes so the
  // canvas always reflects the latest in-flight edits.
  const canvasData: WpsPreviewTemplateData = {
    ...response.data,
    identifiers: {
      ...response.data.identifiers,
      wpsNumber: staged.identifiers.wpsNumber || response.data.identifiers.wpsNumber,
      revision: staged.identifiers.revision || response.data.identifiers.revision
    },
    topMeta: response.data.topMeta.map(
      (row) =>
        row.map((cell) => {
          if (cell === null) return null
          if (cell.label === 'Application:') {
            return { ...cell, value: staged.identifiers.application || cell.value }
          }
          if (cell.label === 'Equipment:') {
            return { ...cell, value: staged.identifiers.equipmentDescription || cell.value }
          }
          return cell
        }) as WpsPreviewTemplateData['topMeta'][number]
    ),
    approvals: staged.approvals.map((a) => ({
      id: a.id,
      label: a.label,
      name: a.name,
      title: a.title,
      date: a.date,
      signatureImage: a.signature,
      stampImage: a.stampImage,
      memberId: a.memberId
    })),
    notes: staged.notes
  }

  // Surface why Save & Generate is blocked rather than greying the
  // button silently. Slice C.1 keeps this reason rendered inline below
  // the button and re-uses the same string for the click-time toast.
  const saveAndGenerateDisabledReason: string | undefined = (() => {
    if (!staged.identifiers.wpsNumber.trim() || !staged.identifiers.revision.trim()) {
      return 'WPS No. and Revision are required to generate the PDF.'
    }
    return undefined
  })()

  const previewLocationLine = joinMetaParts([
    project.location,
    pipeSet ? formatPipeSetDimensions(pipeSet) : null
  ])

  return (
    <div className="space-y-4">
      <PageHeader
        backTo={{ onClick: () => navigate(-1), label: 'Back' }}
        title={<ProjectPageTitle label="WPS Preview" projectName={project.name} />}
        subtitle={
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {previewLocationLine ? <span>{previewLocationLine}</span> : null}
            <WpsPreviewHeaderPills
              pqrNumbers={activePqrNumbers}
              weldIds={activeWeldIds}
              bevelLabel={activeWeldIds.length === 0 ? activeBevelLabel : undefined}
              wpsNumber={staged.identifiers.wpsNumber}
              revision={staged.identifiers.revision}
            />
          </div>
        }
        actions={
          <WpsPreviewToolbar
            isDirty={hasSignificantChanges}
            busy={submitting}
            saveAndGenerateDisabledReason={saveAndGenerateDisabledReason}
            onDiscard={handleDiscard}
            onSave={() => requestSave('save')}
            onSaveAndGenerate={() => requestSave('saveAndGenerate')}
          />
        }
      />

      {isPdfImportPreview ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-medium">PDF import preview</p>
          <p className="mt-1">
            Data was extracted from your uploaded PDF. Review and edit any field below, then use{' '}
            <strong>Save</strong> to persist (no PDF is generated until you choose Save &amp;
            Generate later). Empty or missing extracted values are shown as — in the canvas.
          </p>
        </div>
      ) : null}

      {staleBanner ? (
        <div
          className="flex flex-col gap-2 rounded-lg border border-hero-red-30 bg-hero-red-10 px-4 py-3 text-sm text-hero-red-90 sm:flex-row sm:items-center sm:justify-between"
          role="status"
          data-testid="wps-stale-banner"
        >
          <span>{staleBanner}</span>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-md border border-hero-red-30 bg-white px-3 py-1 text-xs font-semibold text-hero-red-90 hover:bg-hero-red-20"
              onClick={() => {
                setPageConfirm({
                  title: 'Reload server baseline?',
                  description:
                    'The server has a newer version of this WPS. Reloading will discard your staged edits and pull the latest values.',
                  confirmLabel: 'Reload',
                  tone: 'danger',
                  onConfirm: () => {
                    setPageConfirm(null)
                    setStaleBanner(null)
                    void load()
                  }
                })
              }}
              data-testid="wps-stale-banner-reload"
            >
              Reload server data
            </button>
            <button
              type="button"
              className="rounded-md px-2 py-1 text-xs font-medium text-hero-red-90 underline"
              onClick={() => setStaleBanner(null)}
              data-testid="wps-stale-banner-dismiss"
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto" data-testid="wps-preview-canvas-scroll">
        <WpsPreviewCanvas
          data={canvasData}
          fieldLinkContext={
          projectId && pipeSetId
            ? ({
                projectId,
                pipeSetId,
                previewUrl: location.pathname + location.search,
                onModalOpen: (modalKey) => {
                  if (modalKey === 'identifiers') setEditingIdentifiers(true)
                  if (modalKey === 'approvals') setEditingApprovals(true)
                },
                requestNavigate: (request) => {
                  // Slice C.4 of wps-product-parity-fixes — clean-
                  // session per-tab suppression. Once the user ticks
                  // "Don't ask again this session" on the navigate
                  // modal, every subsequent clean-state click jumps
                  // straight through to navigate. Dirty clicks
                  // always prompt — staged work is too easy to lose
                  // to suppress silently.
                  const suppressed =
                    typeof window !== 'undefined' &&
                    window.sessionStorage.getItem(
                      'wps-studio:wps-preview-nav-suppress'
                    ) === '1'
                  if (!hasSignificantChanges && suppressed) {
                    request.proceed()
                    return
                  }
                  setNavRequest(request)
                }
              } satisfies WpsFieldLinkContext)
            : null
          }
        />
      </div>

      <WpsIdentifiersModal
        open={editingIdentifiers}
        initial={staged.identifiers}
        onApply={(next) => {
          setStaged((s) => (s ? { ...s, identifiers: next } : s))
          setEditingIdentifiers(false)
        }}
        onCancel={() => setEditingIdentifiers(false)}
      />

      <WpsApprovalsModal
        open={editingApprovals}
        initial={staged.approvals}
        members={members}
        teamMembersHref={
          projectId ? `/projects/${projectId}/team-members` : '/team-members'
        }
        onApply={(next) => {
          setStaged((s) => (s ? { ...s, approvals: next } : s))
          setEditingApprovals(false)
        }}
        onCancel={() => setEditingApprovals(false)}
      />

      <WpsNavigateConfirmModal
        open={navRequest !== null}
        fieldLabel={navRequest?.fieldLabel ?? ''}
        destinationLabel={navRequest?.target.destinationLabel ?? ''}
        dirty={hasSignificantChanges}
        onCancel={() => setNavRequest(null)}
        onContinue={({ suppressFuture }) => {
          if (suppressFuture && typeof window !== 'undefined') {
            window.sessionStorage.setItem(
              'wps-studio:wps-preview-nav-suppress',
              '1'
            )
          }
          const req = navRequest
          setNavRequest(null)
          req?.proceed()
        }}
        onDiscardAndContinue={() => {
          performDiscard()
          const req = navRequest
          setNavRequest(null)
          req?.proceed()
        }}
        onSaveAndContinue={async () => {
          try {
            await submit(false)
            const req = navRequest
            setNavRequest(null)
            req?.proceed()
            return true
          } catch {
            return false
          }
        }}
      />

      <WpsDiffConfirmDialog
        open={confirmMode !== null}
        busy={submitting}
        diff={significantDiff}
        firstSave={
          !baseline ||
          (baseline.identifiers.wpsNumber.trim() === '' &&
            baseline.identifiers.revision.trim() === '')
        }
        snapshot={staged}
        mode={confirmMode ?? 'save'}
        emailOnly={confirmEmailOnly}
        onCancel={closeConfirmDialog}
        onConfirm={async ({ notificationEmails }) => {
          const mode = confirmMode
          if (mode === null) return
          await submit(mode === 'saveAndGenerate', { notificationEmails })
          closeConfirmDialog()
        }}
      />

      <ConfirmDialog
        open={pageConfirm !== null}
        title={pageConfirm?.title ?? ''}
        description={pageConfirm?.description ?? ''}
        confirmLabel={pageConfirm?.confirmLabel ?? 'Confirm'}
        tone={pageConfirm?.tone}
        onConfirm={() => pageConfirm?.onConfirm()}
        onCancel={() => setPageConfirm(null)}
      />

      {viewer ? (
        <PdfViewer
          fileUrl={viewer.fileUrl}
          fileName={viewer.fileName}
          onClose={() => setViewer(null)}
        />
      ) : null}
    </div>
  )
}
